/**
 * Curved text drawn from real glyph outlines, sharing Typography Pro's engine
 * (`src/shared/curveText`).
 *
 * The legacy renderer (`src/shared/arcText`) lays out one `<text>` per character and lets the
 * browser draw it: that splits ligatures and combining marks, and an exported SVG only matches
 * the canvas if the font CSS is inlined in time. Once the font file is parsed, this renderer
 * places fontkit-shaped glyph paths on the same arc instead (same curve convention, same
 * centring, same padding), and the legacy one only covers the moment before the face loads.
 */
import { create } from 'fontkit';
import { configureCurveFonts, ensureFace, faceSupport, getFace, normalizeStyle, normalizeWeight, type FaceRequest, type FaceStyle, type FontkitFont } from '@shared/curveText/fontEngine';
import { arcPath } from '@shared/curveText/pathGeometry';
import { balancedRunCenter, escapeXml, glyphMarkup, placeOnPath, runInkBounds, shapeRun, unitsPerEm } from '@shared/curveText/placeRun';
import { renderArcText } from '../../../src/shared/arcText/ArcRenderer';
import { buildArcSvgString, measureArcTextBounds } from '../../../src/shared/arcText/ArcLayout';

export interface CurvedTextItem {
  id?: string;
  type?: string;
  text?: unknown;
  color?: unknown;
  opacity?: unknown;
  fontFamily?: unknown;
  fontSize?: unknown;
  fontWeight?: unknown;
  fontStyle?: unknown;
  letterSpacing?: unknown;
  textCurve?: unknown;
  textCurveReverse?: unknown;
  textCurveSpacing?: unknown;
  textCurvePreview?: unknown;
  typography?: unknown;
  [key: string]: unknown;
}

async function bundledFace(family: string, weight: string, style: FaceStyle): Promise<string> {
  const name = family.toLowerCase();
  if (name === 'roboto' && style === 'normal') {
    if (weight === '400') return (await import('@fontsource/roboto/files/roboto-latin-400-normal.woff2?inline')).default;
    if (weight === '700') return (await import('@fontsource/roboto/files/roboto-latin-700-normal.woff2?inline')).default;
    if (weight === '900') return (await import('@fontsource/roboto/files/roboto-latin-900-normal.woff2?inline')).default;
    return '';
  }
  // Arial is not on Google Fonts; Arimo is its metric-compatible twin.
  if (name !== 'arial') return '';
  const bold = Number(weight) >= 600;
  if (!bold && style === 'normal') return (await import('@fontsource/arimo/files/arimo-latin-400-normal.woff2?inline')).default;
  if (!bold) return (await import('@fontsource/arimo/files/arimo-latin-400-italic.woff2?inline')).default;
  if (style === 'normal') return (await import('@fontsource/arimo/files/arimo-latin-700-normal.woff2?inline')).default;
  return (await import('@fontsource/arimo/files/arimo-latin-700-italic.woff2?inline')).default;
}

configureCurveFonts({ create: (data) => create(data), bundled: bundledFace });

function n(value: unknown, fallback: number): number {
  const x = Number(value);
  return Number.isFinite(x) ? x : fallback;
}

function normalizeText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function clampCurve(value: unknown): number {
  return Math.max(-100, Math.min(100, n(value, 0)));
}

export function isCurvedText(item: CurvedTextItem | null | undefined): boolean {
  return item?.type === 'text' && Number(item?.textCurve ?? 0) !== 0;
}

export function curvedFaceRequest(item: CurvedTextItem): FaceRequest {
  return {
    family: String(item.fontFamily ?? 'Arial'),
    weight: normalizeWeight(item.fontWeight),
    style: normalizeStyle(item.fontStyle),
    text: normalizeText(item.text),
  };
}

type OutlineArc = { width: number; height: number; body: string; guide: string };

function xHeight(font: FontkitFont, fontSize: number): number {
  const units = Number((font as { xHeight?: number }).xHeight) || unitsPerEm(font) * 0.5;
  return units * fontSize / unitsPerEm(font);
}

/** Outline layout of a curved text item, or null while its face is not parsed yet. */
export function buildOutlineArc(item: CurvedTextItem): OutlineArc | null {
  const text = normalizeText(item.text);
  const curve = clampCurve(item.textCurve);
  if (!text || curve === 0) return null;
  const font = getFace(curvedFaceRequest(item));
  if (!font) return null;

  const fontSize = n(item.fontSize, 24);
  const spacing = n(item.letterSpacing, 0) + n(item.textCurveSpacing, 0);
  const shaped = shapeRun(font, text, fontSize, spacing);
  if (shaped.total <= 0) return null;
  const signDir: 1 | -1 = curve < 0 ? -1 : 1;
  const dir: 1 | -1 = item.textCurveReverse ? (-signDir as 1 | -1) : signDir;
  // textCurve ±100 is a full turn, the same mapping Typography Pro mirrors as `arcAngle / 3.6`.
  const angle = Math.abs(curve) / 100 * Math.PI * 2;
  const path = arcPath(0, 0, shaped.total / Math.max(0.001, angle), dir);
  // The legacy renderer centres each glyph on the arc with `dominant-baseline="middle"`, i.e.
  // half an x-height above the baseline. Keep that so a design does not jump when the face loads.
  const shift = xHeight(font, fontSize) / 2;
  const runCenter = balancedRunCenter(shaped.glyphs, shaped.total, path, dir, shift);
  const glyphs = placeOnPath(shaped.glyphs, path, runCenter, shift);
  const ink = runInkBounds(glyphs);
  if (![ink.minX, ink.minY, ink.maxX, ink.maxY].every(Number.isFinite)) return null;

  const padding = Math.max(4, fontSize * 0.12);
  const offsetX = -ink.minX + padding;
  const offsetY = -ink.minY + padding;
  const width = Math.max(1, ink.maxX - ink.minX + padding * 2);
  const height = Math.max(1, ink.maxY - ink.minY + padding * 2);

  // Dashed guide along the painted stretch of the arc, through the glyph slot midpoints.
  const steps = 48;
  const from = -runCenter;
  const to = shaped.total - runCenter;
  const guide = Array.from({ length: steps + 1 }, (_, index) => {
    const point = path(from + (to - from) * index / steps);
    return `${index ? 'L' : 'M'} ${(point.x + offsetX).toFixed(2)} ${(point.y + offsetY).toFixed(2)}`;
  }).join(' ');

  return { width, height, body: glyphMarkup(glyphs, offsetX, offsetY), guide };
}

function opacityOf(item: CurvedTextItem): number {
  return Number.isFinite(Number(item.opacity)) ? Number(item.opacity) : 1;
}

function outlineSvg(item: CurvedTextItem, layout: OutlineArc, standalone: boolean): string {
  const size = standalone ? `width="${layout.width}" height="${layout.height}"` : 'width="100%" height="100%"';
  const guide = !standalone && item.textCurvePreview
    ? `<path d="${layout.guide}" fill="none" stroke="#38bdf8" stroke-width="${Math.max(1, n(item.fontSize, 24) * 0.04)}" stroke-dasharray="6 6" stroke-linecap="round" opacity="0.55" style="pointer-events:none"/>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${layout.width.toFixed(3)} ${layout.height.toFixed(3)}" ${size}>`
    + `<g fill="${escapeXml(String(item.color ?? '#000'))}" opacity="${opacityOf(item)}" style="pointer-events:visiblePainted">${layout.body}</g>${guide}</svg>`;
}

function mount(item: CurvedTextItem, layout: OutlineArc, container: HTMLElement): () => void {
  const host = document.createElement('div');
  host.innerHTML = outlineSvg(item, layout, false);
  const svg = host.firstElementChild as SVGSVGElement | null;
  if (!svg) return () => {};
  svg.style.overflow = 'visible';
  svg.style.position = 'absolute';
  svg.style.left = '0';
  svg.style.top = '0';
  // Allow pointer events so clicking glyphs selects the item; events bubble to the host canvas.
  svg.style.pointerEvents = 'auto';
  container.style.position = container.style.position || 'relative';
  container.style.overflow = 'visible';
  container.appendChild(svg);
  return () => svg.remove();
}

export function renderCurvedText(item: CurvedTextItem, container: HTMLElement): () => void {
  const layout = buildOutlineArc(item);
  if (layout) return mount(item, layout, container);
  // Face still loading: draw with the browser meanwhile, then swap in the outlines.
  let cleanup = renderArcText(item as never, container);
  let active = true;
  void ensureFace(curvedFaceRequest(item)).then((font) => {
    if (!active || !font) return;
    const next = buildOutlineArc(item);
    if (!next) return;
    cleanup();
    cleanup = mount(item, next, container);
  });
  return () => {
    active = false;
    cleanup();
  };
}

export function measureCurvedText(item: CurvedTextItem): { width: number; height: number } | null {
  const layout = buildOutlineArc(item);
  return layout ? { width: layout.width, height: layout.height } : measureArcTextBounds(item as never);
}

export function prepareCurvedText(item: CurvedTextItem): Promise<unknown> {
  return ensureFace(curvedFaceRequest(item));
}

export function exportCurvedText(item: CurvedTextItem, ctx?: { fontFaceCss?: string }): string {
  const layout = buildOutlineArc(item);
  return layout ? outlineSvg(item, layout, true) : buildArcSvgString(item as never, { fontFaceCss: ctx?.fontFaceCss });
}

export function curvedTextStyleSupport(item: CurvedTextItem) {
  return faceSupport(curvedFaceRequest(item));
}
